/**
 * Pure helpers for the flight "Where to book" provider list, shared by the web
 * `GoogleFlightBookingOptions` and the native `FlightBookingOptions` (ELARA-717).
 *
 * The only cross-platform logic here is picking the provider option whose total
 * price most closely matches the flight option the traveler was shown, so the UI
 * can surface it first as the "matched" recommendation. Kept DOM-free and
 * model-agnostic (operates on any `{ cost }`-shaped value) so it can be unit
 * tested once and reused on both platforms.
 */

import type { Cost } from '@elara/domain/models/schemas/shared-schemas';

/** A booking option's price may differ from the displayed flight price by at most 2% to count as a match. */
const COST_MATCH_TOLERANCE = 0.02;

/**
 * Relative price distance between a candidate and the displayed flight price, or
 * `undefined` when the currencies differ or the candidate is outside tolerance.
 */
function matchRatio(candidate: Cost, target: Cost): number | undefined {
  if (candidate.currency.toLowerCase() !== target.currency.toLowerCase()) return undefined;
  const ratio = Math.abs(candidate.amount - target.amount) / target.amount;
  return ratio <= COST_MATCH_TOLERANCE ? ratio : undefined;
}

/**
 * Returns the option whose price most closely matches the displayed flight price
 * (same currency, within tolerance), or `undefined` when none qualify. The
 * returned value is one of the input elements, so callers can filter by identity.
 */
export function findClosestBookingMatch<T extends { cost: Cost }>(
  options: readonly T[],
  target: { cost?: Cost } | undefined,
): T | undefined {
  const targetCost = target?.cost;
  if (!targetCost) return undefined;

  let best: T | undefined;
  let bestRatio = Infinity;
  for (const option of options) {
    const ratio = matchRatio(option.cost, targetCost);
    if (ratio != null && ratio < bestRatio) {
      best = option;
      bestRatio = ratio;
    }
  }
  return best;
}
